/* =============================================================================
   VIEWS / ADMIN / STOCK-ADMIN — Lager / Bestandsführung (Phase E1)
   =============================================================================
   Subtab „Lager" der Gruppe „Ware" (S157). Bestand je Zutat (Σ Ledger), Mindest-
   bestand + Bestell-Alarm, manuelle Buchungen (Wareneingang/Korrektur/Schwund/
   Inventur) + Verlauf. Alles über SECURITY-DEFINER-RPCs (Migration phase_118);
   Schreiben ist server-fest auf Manager beschränkt (assert_operator_role) — die UI
   zeigt die Buttons trotzdem (Server gated hart, Fehlertoast bei fehlender Rolle).

   Bestand in BASISEINHEITEN; benannte Einheiten werden beim Buchen via
   qtyInBaseUnits (food-cost.js) umgerechnet. Hinter Flag `ingredientsDb`.
   Non-destruktive Renders; Inline-Handler ohne window.func() (CSP).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { qtyInBaseUnits } from '../../core/food-cost.js';
import { purchaseUnitToBase } from '../../core/suppliers.js';
import { AMPEL_COLORS } from '../../core/food-cost.js';
import { MOVEMENT_TYPES, stockBand, formatStock, signedQuantity, inventoryDelta, movementLabel,
  formatStockValue, isNegativeValue } from '../../core/stock.js';
import { registerLiveView } from '../../core/live-view-registry.js';

const STOCK_COLOR = { ok: AMPEL_COLORS.green, low: AMPEL_COLORS.red, unknown: AMPEL_COLORS.unknown }; // S166 zentral
const STOCK_LABEL = { ok: 'im Soll', low: 'unter Mindestbestand', unknown: 'kein Mindestbestand gesetzt' };

let _overview = [];           // stock_overview()-Zeilen (camelCase)
let _byId = {};               // ingredientId → overview-Zeile
let _valueTotals = null;      // E3 — stock_value_totals() {totalRaw,totalYield,valuedCount,unvaluedCount}
let _lotsSummary = {};        // S166 — lots_summary() je Zutat (MHD-Badge)
let _booking = null;          // aktueller Buchungs-Draft
let _bookingUnits = [];       // benannte Einheiten der gebuchten Zutat
let _bookingSuppliers = [];   // E4a — Lieferanten-Verknüpfungen der Zutat (Einkaufseinheit + Provenienz)

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

// -----------------------------------------------------------------------------
// Tab-Render
// -----------------------------------------------------------------------------
// S161 — Live-View-Registry: ein Verkauf bucht den Bestand per DB-Trigger ab
// (E2). Das Order-Realtime-Event markiert den (ggf. verborgenen) Lager-Tab dirty →
// beim Sichtbarwerden (Rückkehr aus KDS/Theke) rendert er frisch, ohne harten Reload.
// Lazy registriert (App.events liegt erst zur Laufzeit vor). Memory gg-no-view-hijack.
let _stockLiveRegistered = false;
function ensureStockLiveRegistered() {
  if (_stockLiveRegistered) return;
  _stockLiveRegistered = true;
  registerLiveView({
    tabId: 'tab-lager',
    render: renderStockAdmin,
    events: ['order:realtime', 'order:status_changed', 'order:created']
  });
}

export async function renderStockAdmin() {
  ensureStockLiveRegistered();
  const el = document.getElementById('tab-lager');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Das Lager ist deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Lager…</p></div>`;
  try {
    _overview = await storage().stock.overview();
  } catch (e) {
    console.warn('[stock] overview:', e);
    _overview = [];
  }
  // E3 — Wert-Summen separat (genaue Σ inkl. nicht angezeigter Randfälle).
  try {
    _valueTotals = await storage().stock.valueTotals();
  } catch (e) {
    console.warn('[stock] valueTotals:', e);
    _valueTotals = null;
  }
  // S166 — MHD/Chargen-Aggregat je Zutat (Badge); Vorlauf aus Settings.
  try {
    const st = await storage().settings.get();
    const v = st && st.mhdVorlaufTage;
    const vorlauf = (v != null && isFinite(Number(v)) && Number(v) >= 0) ? Number(v) : 3;
    const sum = await storage().lots.summary(vorlauf);
    _lotsSummary = Object.fromEntries((sum || []).map(s => [s.ingredientId, s]));
  } catch (e) {
    console.warn('[stock] lots.summary:', e);
    _lotsSummary = {};
  }
  _byId = Object.fromEntries(_overview.map(r => [r.ingredientId, r]));
  el.innerHTML = renderListHtml();
}

function renderListHtml() {
  const negatives = _overview.filter(r => r.isNegative);
  const alarms = _overview.filter(r => r.belowMin && !r.isNegative);
  const negHtml = negatives.length ? `
    <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--danger-soft,#fbe3e3); border:1px solid var(--danger,#b94a48);">
      <strong style="color:var(--danger,#b94a48);">⛔ ${negatives.length} Zutat(en) mit NEGATIVEM Bestand — nachpflegen:</strong>
      <span class="hint" style="font-size:.85rem;">${negatives.map(r => htmlEscape(r.name)).join(' · ')}</span>
    </div>` : '';
  const alarmHtml = alarms.length ? `
    <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--warn-soft,#fff4e0); border:1px solid var(--warn,#d9a227);">
      <strong style="color:var(--warn);">⚠️ ${alarms.length} Zutat(en) unter Mindestbestand:</strong>
      <span class="hint" style="font-size:.85rem;">${alarms.map(r => htmlEscape(r.name)).join(' · ')}</span>
    </div>` : '';

  const rows = _overview.map(renderRowHtml).join('');
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Lager / Bestand</h2>
          <p class="hint" style="margin:4px 0 0;">Bestand je Zutat (Σ Bewegungen) in Basiseinheit. Verkauf bucht automatisch ab. Buchungen sind Manager-Aktionen. ${_overview.length} Zutat(en).</p>
        </div>
      </div>
      ${negHtml}
      ${alarmHtml}
      ${renderValueSummaryHtml()}
      <div style="margin-top:14px; overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:.9rem;">
          <thead><tr style="text-align:left; color:var(--ink-soft,#666);">
            <th style="padding:6px 8px;">Zutat</th>
            <th style="padding:6px 8px; text-align:right;">Bestand</th>
            <th style="padding:6px 8px; text-align:right;" title="Bestand × roher Einkaufspreis (Inventarwert — was physisch im Regal liegt)">Lagerwert</th>
            <th style="padding:6px 8px; text-align:right; font-weight:400;" title="Bestand × ausbeute-bereinigte Kosten — Indikator, kein Bilanzwert">Nutzwert <span style="font-size:.74rem;">(Ausbeute)</span></th>
            <th style="padding:6px 8px; text-align:right;">Mindestbestand</th>
            <th style="padding:6px 8px; text-align:center;">Status</th>
            <th style="padding:6px 8px;"></th>
          </tr></thead>
          <tbody>${rows || '<tr><td colspan="7" class="hint" style="padding:16px; text-align:center;">Noch keine Zutaten. Erst im Tab „Zutaten" anlegen.</td></tr>'}</tbody>
        </table>
      </div>
    </div>`;
}

// E3 — Wert-Summen: Lagerwert (primär, hervorgehoben) + Nutzwert (sekundär, dezent) +
// „nicht bewertet"-Zähler. Quelle: stock_value_totals() (genaue Σ). Fehlt der RPC → kein Block.
function renderValueSummaryHtml() {
  const t = _valueTotals;
  if (!t) return '';
  const rawNeg = isNegativeValue(t.totalRaw) ? 'color:var(--danger,#b94a48);' : '';
  const unvalued = Number(t.unvaluedCount) > 0
    ? `<span class="hint" style="font-size:.82rem;">${t.unvaluedCount} Zutat(en) ohne Einkaufspreis — nicht bewertet</span>`
    : '';
  // S203 Cut B — chargen-genauer Teil des Lagerwerts (phase_152): der in Chargen
  // geführte Bestand zum tatsächlichen Charge-EK (Fallback Listenpreis). Nur zeigen,
  // wenn überhaupt aktive Chargen existieren — sonst dominiert der Non-Lot-Bestand
  // (Listenpreis-Headline links) und der Block wäre Rauschen.
  const lotCount = Number(t.lotValuedCount || 0) + Number(t.lotFallbackCount || 0);
  const lotBlock = lotCount > 0
    ? `
      <div style="color:var(--ink-soft,#7a807a);">
        <div class="hint" style="font-size:.76rem; text-transform:uppercase; letter-spacing:.04em;">davon chargen-genau (Charge-EK)</div>
        <div style="font-size:1.05rem; font-weight:600;">${formatStockValue(t.totalLotCost)}</div>
        <div class="hint" style="font-size:.72rem;">${Number(t.lotValuedCount || 0)} Charge(n) zum Charge-EK${Number(t.lotFallbackCount || 0) > 0 ? ` · ${t.lotFallbackCount} zum Listenpreis` : ''}</div>
      </div>`
    : '';
  return `
    <div style="margin-top:14px; padding:12px 16px; border-radius:10px; background:var(--bg-soft); border:1px solid var(--border,#e3e7e3); display:flex; gap:28px; align-items:baseline; flex-wrap:wrap;">
      <div>
        <div class="hint" style="font-size:.78rem; text-transform:uppercase; letter-spacing:.04em;">Lagerwert gesamt</div>
        <div style="font-size:1.5rem; font-weight:700; ${rawNeg}">${formatStockValue(t.totalRaw)}</div>
        <div class="hint" style="font-size:.72rem;">Bestand × Einkaufspreis (Inventarwert)</div>
      </div>
      <div style="color:var(--ink-soft,#7a807a);">
        <div class="hint" style="font-size:.76rem; text-transform:uppercase; letter-spacing:.04em;">Nutzwert (mit Ausbeute)</div>
        <div style="font-size:1.05rem; font-weight:500;">${formatStockValue(t.totalYield)}</div>
        <div class="hint" style="font-size:.72rem;">Indikator — kein Bilanzwert</div>
      </div>
      ${lotBlock}
      <div style="margin-left:auto; align-self:center;">${unvalued}</div>
    </div>`;
}

// S166 — MHD/Chargen-Badge je Zutat (aus lots_summary). Klick → Chargen-Tab.
function renderLotBadge(ingredientId) {
  const s = _lotsSummary[ingredientId];
  if (!s || !Number(s.activeLots)) return '';
  const color = s.hasExpired ? AMPEL_COLORS.red : (s.hasNear ? AMPEL_COLORS.yellow : AMPEL_COLORS.green);
  const title = s.hasExpired ? `${s.activeLots} Charge(n) · MHD abgelaufen — zu Chargen`
    : (s.hasNear ? `${s.activeLots} Charge(n) · MHD läuft bald ab — zu Chargen`
      : `${s.activeLots} aktive Charge(n) — zu Chargen`);
  return ` <span onclick="switchTab('lots')" title="${htmlEscape(title)}" style="cursor:pointer; display:inline-block; margin-left:6px; padding:1px 6px; border-radius:8px; font-size:.68rem; font-weight:600; color:#fff; background:${color};">⏲ ${s.activeLots}</span>`;
}

function renderRowHtml(r) {
  const band = stockBand(r.stockBase, r.minStockBase);
  const dot = `<span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:${STOCK_COLOR[band]};" title="${STOCK_LABEL[band]}"></span>`;
  const baseLabel = r.baseUnit === 'piece' ? 'Stk' : (r.baseUnit || '');
  const stockStyle = r.isNegative ? 'color:var(--danger,#b94a48); font-weight:700;' : '';
  const negNote = r.isNegative ? ' <span class="hint" style="color:var(--danger,#b94a48); font-size:.74rem;">negativ — nachpflegen</span>' : '';
  // E3 — Lagerwert (primär) + Nutzwert (sekundär, dezent). null → „—" (nicht 0 €); negativ → rot.
  const rawStyle = isNegativeValue(r.valueRaw) ? 'color:var(--danger,#b94a48); font-weight:600;' : '';
  const yieldStyle = isNegativeValue(r.valueYield) ? 'color:var(--danger,#b94a48);' : 'color:var(--ink-soft,#8a908a);';
  return `
    <tr style="border-top:1px solid var(--border,#eee);">
      <td style="padding:6px 8px; font-weight:600;">${htmlEscape(r.name)}${renderLotBadge(r.ingredientId)}</td>
      <td style="padding:6px 8px; text-align:right; ${stockStyle}">${formatStock(r.stockBase, r.baseUnit)}${negNote}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap; ${rawStyle}">${formatStockValue(r.valueRaw)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap; font-size:.82rem; ${yieldStyle}">${formatStockValue(r.valueYield)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">
        <input type="number" step="1" min="0" value="${r.minStockBase != null ? r.minStockBase : ''}" placeholder="—"
               style="width:80px; text-align:right;" onchange="stockSetMin('${htmlEscape(r.ingredientId)}', this.value)">
        <span class="hint" style="font-size:.78rem;">${htmlEscape(baseLabel)}</span>
      </td>
      <td style="padding:6px 8px; text-align:center;">${dot}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">
        <button class="btn btn-secondary btn-sm" onclick="stockOpenBooking('${htmlEscape(r.ingredientId)}')">Buchen</button>
        <button class="btn btn-ghost btn-sm" onclick="stockOpenHistory('${htmlEscape(r.ingredientId)}')">Verlauf</button>
      </td>
    </tr>`;
}

// -----------------------------------------------------------------------------
// Mindestbestand inline setzen
// -----------------------------------------------------------------------------
export async function stockSetMin(ingredientId, value) {
  const v = (value === '' || value == null) ? null : Number(value);
  if (v != null && (!isFinite(v) || v < 0)) { showToast('Mindestbestand muss ≥ 0 sein', 'error'); return; }
  try {
    await storage().stock.setMin(ingredientId, v);
    await renderStockAdmin();
  } catch (e) {
    console.error('[stock] setMin:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// Buchungs-Modal
// -----------------------------------------------------------------------------
export async function stockOpenBooking(ingredientId) {
  const r = _byId[ingredientId];
  if (!r) return;
  _booking = { ingredientId, type: 'purchase', quantity: '', unit: 'base', note: '', supplierId: '', mhd: '' };
  _bookingUnits = [];
  _bookingSuppliers = [];
  try { _bookingUnits = await storage().ingredientUnits.listFor(ingredientId) || []; } catch (_) { _bookingUnits = []; }
  try { _bookingSuppliers = await storage().ingredientSuppliers.listFor(ingredientId) || []; } catch (_) { _bookingSuppliers = []; }
  // Default-Provenienz = Hauptlieferant (nur Vorbelegung; frei änderbar)
  const primary = _bookingSuppliers.find(s => s.isPrimary);
  if (primary) _booking.supplierId = primary.supplierId;
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = renderBookingHtml(r);
  overlay.classList.add('active');
}

function renderBookingHtml(r) {
  const baseLabel = r.baseUnit === 'piece' ? 'Stück' : (r.baseUnit || 'Basis');
  const isInv = _booking.type === 'inventory';
  // Einheiten: Basis + benannte Einheiten + Einkaufseinheiten der Lieferanten (E4a, value="sup:<isupId>").
  const supUnitOpts = _bookingSuppliers
    .filter(s => s.purchaseUnit && Number(s.unitQtyBase) > 0)
    .map(s => `<option value="sup:${htmlEscape(s.id)}" ${_booking.unit === 'sup:' + s.id ? 'selected' : ''}>${htmlEscape(s.purchaseUnit)} · ${htmlEscape(s.supplierName || '')} (= ${htmlEscape(String(s.unitQtyBase))} ${htmlEscape(baseLabel)})</option>`).join('');
  const unitOpts = `<option value="base">${htmlEscape(baseLabel)} (Basis)</option>` +
    _bookingUnits.map(u => `<option value="${htmlEscape(u.unitName)}" ${_booking.unit === u.unitName ? 'selected' : ''}>${htmlEscape(u.unitName)}</option>`).join('') +
    supUnitOpts;
  // Lieferanten-Provenienz (nur bei Wareneingang sinnvoll, optional) — distinct supplier_id.
  const supSeen = {};
  const provOpts = `<option value="">— ohne Lieferant —</option>` + _bookingSuppliers.reduce((acc, s) => {
    if (s.supplierId && !supSeen[s.supplierId]) { supSeen[s.supplierId] = 1;
      acc += `<option value="${htmlEscape(s.supplierId)}" ${_booking.supplierId === s.supplierId ? 'selected' : ''}>${htmlEscape(s.supplierName || s.supplierId)}</option>`;
    }
    return acc;
  }, '');
  const provRow = (_booking.type === 'purchase' && _bookingSuppliers.length) ? `
    <div class="field"><label>Lieferant (Herkunft, optional)</label>
      <select onchange="stockBookingSet('supplierId', this.value)">${provOpts}</select></div>` : '';
  // E6a — MHD optional beim Wareneingang → erzeugt eine Charge (sonst keine).
  const mhdRow = (_booking.type === 'purchase') ? `
    <div class="field"><label>MHD (optional) <small style="color:var(--ink-soft);">— mit MHD entsteht eine Charge</small></label>
      <input type="date" value="${htmlEscape(_booking.mhd || '')}" oninput="stockBookingSet('mhd', this.value)"></div>` : '';
  return `
    <div class="modal-header">
      <h2>Lager buchen · ${htmlEscape(r.name)}</h2>
      <button class="modal-close" onclick="stockCancel()">×</button>
    </div>
    <p class="hint" style="margin:0 0 10px;">Aktueller Bestand: <strong>${formatStock(r.stockBase, r.baseUnit)}</strong></p>
    <div class="field-row">
      <div class="field"><label>Buchungstyp</label>
        <select onchange="stockBookingSetType(this.value)">
          ${MOVEMENT_TYPES.map(t => `<option value="${t.type}" ${_booking.type === t.type ? 'selected' : ''}>${t.label}</option>`).join('')}
        </select></div>
      <div class="field"><label>${isInv ? 'Gezählter Bestand' : 'Menge'}</label>
        <input id="stk-qty" type="number" step="0.01" value="${_booking.quantity}" oninput="stockBookingSet('quantity', this.value)"></div>
      <div class="field"><label>Einheit</label>
        <select onchange="stockBookingSet('unit', this.value)">${unitOpts}</select></div>
    </div>
    ${provRow}
    ${mhdRow}
    <div class="field"><label>Notiz (optional)</label>
      <input type="text" value="${htmlEscape(_booking.note)}" oninput="stockBookingSet('note', this.value)" placeholder="z. B. Lieferung Metro / Inventur 06/2026"></div>
    <p id="stk-preview" class="hint" style="font-size:.82rem;">${bookingPreview(r)}</p>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-primary" onclick="stockSaveBooking()">Buchen</button>
      <button class="btn btn-ghost" onclick="stockCancel()">Abbrechen</button>
    </div>`;
}

// E4a — Menge→Basiseinheiten auflösen: Basis/benannte Einheit via qtyInBaseUnits,
// Lieferanten-Einkaufseinheit ("sup:<isupId>") via purchaseUnitToBase(qty, unit_qty_base).
function resolveBookingQtyBase() {
  const q = Number(_booking.quantity);
  if (typeof _booking.unit === 'string' && _booking.unit.startsWith('sup:')) {
    const isupId = _booking.unit.slice(4);
    const link = _bookingSuppliers.find(s => s.id === isupId);
    return link ? purchaseUnitToBase(q, link.unitQtyBase) : null;
  }
  return qtyInBaseUnits(q, _booking.unit, _bookingUnits);
}

// Vorschau: bei Inventur die Differenz, sonst die signierte Wirkung.
function bookingPreview(r) {
  const qtyBase = resolveBookingQtyBase();
  if (qtyBase == null) return 'Menge eingeben…';
  if (_booking.type === 'inventory') {
    const d = inventoryDelta(qtyBase, r.stockBase);
    const sign = d > 0 ? '+' : '';
    return `Setzt den Bestand auf ${formatStock(qtyBase, r.baseUnit)} (Differenz ${sign}${formatStock(d, r.baseUnit)}).`;
  }
  const signed = signedQuantity(_booking.type, qtyBase);
  if (signed == null) return 'Menge eingeben…';
  const sign = signed > 0 ? '+' : '';
  return `Bewegung ${sign}${formatStock(signed, r.baseUnit)} → neuer Bestand ${formatStock(r.stockBase + signed, r.baseUnit)}.`;
}

export function stockBookingSet(field, value) {
  if (!_booking) return;
  _booking[field] = value;
  // E4a — beim Wechsel auf eine Lieferanten-Einkaufseinheit die Provenienz mitführen.
  if (field === 'unit' && typeof value === 'string' && value.startsWith('sup:')) {
    const link = _bookingSuppliers.find(s => s.id === value.slice(4));
    if (link && link.supplierId) _booking.supplierId = link.supplierId;
  }
  // Live-Vorschau aktualisieren (kein voller Re-Render → Tipp-Fokus bleibt)
  const r = _byId[_booking.ingredientId];
  const prev = document.getElementById('stk-preview');
  if (prev && r) prev.textContent = bookingPreview(r);
}

export function stockBookingSetType(type) {
  if (!_booking) return;
  _booking.type = type;
  const r = _byId[_booking.ingredientId];
  const content = document.getElementById('modal-content');
  if (content && r) content.innerHTML = renderBookingHtml(r); // Label „Menge/Gezählter Bestand" wechselt
}

export async function stockSaveBooking() {
  if (!_booking) return;
  const r = _byId[_booking.ingredientId];
  const qtyBase = resolveBookingQtyBase();
  if (qtyBase == null) { showToast('Bitte eine gültige Menge/Einheit angeben', 'error'); return; }
  // Provenienz + MHD nur bei Wareneingang mitschreiben (Verbrauch/Schwund/Korrektur: ohne).
  const supplierId = _booking.type === 'purchase' ? (_booking.supplierId || null) : null;
  const mhd = _booking.type === 'purchase' ? (_booking.mhd || null) : null;
  try {
    if (_booking.type === 'inventory') {
      if (qtyBase < 0) { showToast('Gezählter Bestand muss ≥ 0 sein', 'error'); return; }
      await storage().stock.recordInventory(_booking.ingredientId, qtyBase, _booking.note);
    } else {
      const signed = signedQuantity(_booking.type, qtyBase);
      if (signed == null || signed === 0) { showToast('Menge darf nicht 0 sein', 'error'); return; }
      await storage().stock.record(_booking.ingredientId, _booking.type, signed, _booking.note, supplierId, mhd);
    }
    showToast('Bewegung gebucht', 'success');
    _booking = null;
    if (typeof window.closeModal === 'function') window.closeModal();
    await renderStockAdmin();
  } catch (e) {
    console.error('[stock] save booking:', e);
    showToast('Buchen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export function stockCancel() {
  _booking = null;
  if (typeof window.closeModal === 'function') window.closeModal();
}

// -----------------------------------------------------------------------------
// Verlauf-Drilldown
// -----------------------------------------------------------------------------
export async function stockOpenHistory(ingredientId) {
  const r = _byId[ingredientId];
  if (!r) return;
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = `<div class="modal-header"><h2>Verlauf · ${htmlEscape(r.name)}</h2><button class="modal-close" onclick="closeModal()">×</button></div><p class="hint">Lade…</p>`;
  overlay.classList.add('active');
  let rows = [];
  try { rows = await storage().stock.historyFor(ingredientId, 100); } catch (_) { rows = []; }
  const body = rows.map(m => {
    const sign = Number(m.quantityBase) > 0 ? '+' : '';
    const when = m.occurredAt ? new Date(m.occurredAt).toLocaleString('de-DE') : '';
    const isAuto = m.type === 'consumption';
    const lblStyle = isAuto ? (m.source === 'reversal' ? 'color:var(--ink-soft,#888);' : '') : '';
    return `
      <tr style="border-top:1px solid var(--border,#eee);">
        <td style="padding:5px 8px; ${lblStyle}">${htmlEscape(movementLabel(m.type, m.source))}${m.orderId ? ` <span class="hint" style="font-size:.72rem;">#${htmlEscape(String(m.orderId).slice(-6))}</span>` : ''}</td>
        <td style="padding:5px 8px; text-align:right;">${sign}${formatStock(m.quantityBase, r.baseUnit)}</td>
        <td style="padding:5px 8px;">${htmlEscape(m.note || '')}</td>
        <td style="padding:5px 8px;" class="hint">${htmlEscape(m.operatorName || '')}</td>
        <td style="padding:5px 8px;" class="hint" style="white-space:nowrap;">${htmlEscape(when)}</td>
      </tr>`;
  }).join('');
  content.innerHTML = `
    <div class="modal-header"><h2>Verlauf · ${htmlEscape(r.name)}</h2><button class="modal-close" onclick="closeModal()">×</button></div>
    <p class="hint" style="margin:0 0 8px;">Aktueller Bestand: <strong>${formatStock(r.stockBase, r.baseUnit)}</strong></p>
    <div style="max-height:50vh; overflow:auto;">
      <table style="width:100%; border-collapse:collapse; font-size:.86rem;">
        <thead><tr style="text-align:left; color:var(--ink-soft,#666);">
          <th style="padding:5px 8px;">Typ</th><th style="padding:5px 8px; text-align:right;">Menge</th>
          <th style="padding:5px 8px;">Notiz</th><th style="padding:5px 8px;">Operator</th><th style="padding:5px 8px;">Zeit</th>
        </tr></thead>
        <tbody>${body || '<tr><td colspan="5" class="hint" style="padding:14px; text-align:center;">Noch keine Bewegungen.</td></tr>'}</tbody>
      </table>
    </div>
    <div class="btn-row" style="margin-top:12px;"><button class="btn btn-ghost" onclick="closeModal()">Schließen</button></div>`;
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderStockAdmin, stockSetMin, stockOpenBooking, stockBookingSet, stockBookingSetType,
    stockSaveBooking, stockCancel, stockOpenHistory
  });
}
